import { type MouseEvent } from 'react';
import { type FooterNavLink } from '@cx-web/domain-types';
import { trackEvent } from '@cx-web/utils/dataLayer';
import classnames from 'classnames';

import { type CmpProps } from '../../types';

import styles from './FooterGeneralLinks.module.css';

const GeneralLink = ({ link, name }: Pick<FooterNavLink, 'link' | 'name'>) => (
  <a
    className={styles.generalLinksNavItem}
    data-testid="footer-general-link"
    href={link}
    onClick={() => trackEvent('footer_level_three_nav', { selection: name })}
  >
    {name}
  </a>
);

const GeneralLinkWithClickEvent = ({
  name,
  onClick,
}: Partial<FooterNavLink> & { onClick: (e: MouseEvent) => void }) => (
  <button
    aria-label={`Open ${name}`}
    className={styles.generalLinksNavItem}
    data-testid="footer-general-link"
    onClick={onClick}
    type="button"
  >
    {name}
  </button>
);

const shouldNotShowLinks = (links: FooterNavLink[]) => {
  return !links?.length;
};

type FooterGeneralLinksProps = {
  cmpProps?: CmpProps;
  isAdmiralCookieBannerEnabled?: boolean;
  isUkFooterStyle?: boolean;
  levelThree: FooterNavLink[];
};

export const FooterGeneralLinks = ({
  cmpProps,
  isAdmiralCookieBannerEnabled,
  isUkFooterStyle,
  levelThree,
}: FooterGeneralLinksProps) => {
  const isCmpEnabled = isAdmiralCookieBannerEnabled;
  const generalLinks = levelThree || [];

  if (shouldNotShowLinks(generalLinks) && !isCmpEnabled) return null;

  const linksNav = (
    <nav
      aria-label="Footer General Links Navigation"
      className={classnames(styles.generalLinksNavContainer, isUkFooterStyle && styles.generalLinksNavContainerUk)}
    >
      {generalLinks?.map(({ link, name }) => (
        <GeneralLink key={name} link={link} name={name} />
      ))}
      {isCmpEnabled && cmpProps && (
        <GeneralLinkWithClickEvent key={cmpProps.key} name={cmpProps.name} onClick={cmpProps.onClick} />
      )}
    </nav>
  );

  return (
    <section
      aria-label="Footer General Links Section"
      className={classnames(styles.generalLinksWrapper, isUkFooterStyle && styles.generalLinksWrapperUk)}
      data-testid="footer-general-links-container"
    >
      {isUkFooterStyle ? (
        <div className={styles.generalLinksRowUk}>
          {linksNav}
          <div className={styles.generalLinksCopyrightUk} data-testid="footer-copyright">
            © Copyright {new Date().getUTCFullYear()}
          </div>
        </div>
      ) : (
        linksNav
      )}
    </section>
  );
};
